import type { PriceItemType } from 'core/accounting/domain/PriceItemType'
import {
  mapApiToPriceItemType,
  type RawPriceItemType,
} from 'core/accounting/infra/PriceItemTypeMapper'
import { mapApiToAppointment, type RawAppointment } from 'core/appointments/infra/appointmentMapper'
import { type RawContainer } from 'core/container-details/domain/ShipmentContainer'
import { mapApiToContainer } from 'core/container-details/infra/ContainerMapper'
import type { EntityType } from 'core/domain/EntityType'
import type { EquipmentSubtypes } from 'core/domain/Load'
import { type RawManagedBy } from 'core/domain/ManagedBy'
import type { FullMode } from 'core/domain/Mode'
import type { StopType, Stop } from 'core/domain/Stop'
import type { TruckType } from 'core/domain/Truck'
import { type RawLoadMeasures } from 'core/infra/LoadMeasuresMapper'
import { mapApiToManagedBy } from 'core/infra/ManagedByMapper.ts'
import { type PackageType } from 'core/shipping-items/domain/PackageType'
import { type RawItem } from 'core/shipping-items/domain/RawShippingItems'
import { type ShippingItem } from 'core/shipping-items/domain/ShippingItem'
import {
  mapApiToShippingItems,
  mapApiToShippingMeasures,
} from 'core/shipping-items/infra/ShippingItemsMapper'
import { createDate } from 'core/utils/date'

import { type Carrier } from '../domain/Carrier'
import { type Quote } from '../domain/Quote'
import type {
  QuoteAccessorial,
  AccessorialCharge,
  RawAccessorialCharge,
} from '../domain/QuoteAccessorial'
import { type QuoteEquipmentRequirements } from '../domain/QuoteEquipmentRequirements'
import { type RateLine } from '../domain/RateLine'
import { type Shipper } from '../domain/Shipper'
import { type BookNowStatus, type BookNowItem, type SpotQuote } from '../domain/SpotQuote'
import { type SpotQuoteStatus } from '../domain/SpotQuoteStatus'
import {
  mapSpotQuoteShipmentFromApi,
  type RawSpotQuoteApiShipment,
} from './SpotQuoteShipmentMapper'

export type RawSpotQuote = {
  carrier: RawCarrier
  shipper: RawShipper
  carrier_notes: string | null
  flat_rate: string | null
  notes: string | null
  per_mile_rate: number | null
  quote: RawQuote
  status: SpotQuoteStatus
  uuid: string
  negotiation_note: string
  under_negotiation: boolean
  ratelines?: Array<RawRateLine>
  book_now_enabled?: boolean
  shipment_cargo_value: number | null
  submitted_at: string
  managed_by?: RawManagedBy
  created_at: string
  shipment?: RawSpotQuoteApiShipment
}

export type RawCarrier = {
  id: number
  name: string | null
  email: string | null
  dot: string | null
  scac: string | null
  mc: string | null
  entity_type?: EntityType
}

export type RawShipper = {
  id: number
  email: string
  logo_email_url: string | null
  name: string | null
  phone_number: string | null
  company_name: string
}

export type RawQuoteItem = {
  commodity?: string
  weight?: string
  package_type?: PackageType
  package_count?: number
  width?: string
  length?: string
  height?: string
  stackable?: boolean
  turnable?: boolean
  freight_class?: string
  nmfc_code?: string
}

export type RawQuoteStop = {
  address: string | null
  city: string
  country: string
  date: string | null
  state: string
  zipcode: string | null
  full_street_address?: string | null
  facility?: { company_name?: string } | null
  appointment?: RawAppointment
}

export type RawQuote = {
  bids_deadline: string
  can_receive_proposals: boolean
  equipment_type: TruckType
  equipment_requirements?: RawQuoteEquipmentRequirements
  items: Array<RawItem>
  mileage: number | null
  mode: FullMode
  notes?: string | null
  shipper_guide_id: string
  shipper_notes: string | null
  stops: Array<RawQuoteStop>
  accessorials?: Array<RawQuoteAccessorial>
  requested_accessorials?: Array<RawPriceItemType>
  measures: RawLoadMeasures | null
  power_only?: boolean
  target_rate?: string
  managed_by: RawManagedBy
  container?: RawContainer
}

export type RawQuoteEquipmentRequirements = {
  with_tarp: boolean
  tarp_size: string
  tarp_type: string
  dunnage: boolean
  equipment_subtypes?: Array<EquipmentSubtypes>
  temperature?: string
  unit?: string
}

export type RawQuoteAccessorial = {
  name: string
  code: string
  stop_type: Array<StopType>
  modes: Array<FullMode>
}

export type RawRateLine = {
  quantity: string
  subtype?: string
  total: string
  type: {
    value: string
    label: string
  }
  unit_price: string
  uuid: string
  notes?: string
  price_item_type?: Omit<RawPriceItemType, 'stops_relationship'>
  stop_type?: StopType
}

export type SubmitSpotQuoteParams = {
  id: string
  flatRate: number
  notes?: string
  accessorials?: Array<AccessorialCharge>
  requestedAccessorials?: Array<AccessorialCharge>
}

export type RawSubmitSpotQuoteParams = {
  flat_rate: number
  notes: string
  accessorials?: Array<RawAccessorialCharge>
  requested_accessorials?: Array<RawAccessorialCharge>
}

export type RawBookNowItem = {
  uuid: string
  status: 'created' | 'booked' | 'failed'
  status_reason: string | null
  rate: string
  shipment_link: string | null
}

export type RawBookNowStatus = {
  uuid: string
  status: 'created' | 'processing' | 'finished'
  items: Array<RawBookNowItem>
}

export type RejectSpotQuoteParams = {
  id: string
  reason: string
}

export function mapApiToBookNowItem(bookNowItem: RawBookNowItem): BookNowItem {
  return {
    uuid: bookNowItem.uuid,
    status: bookNowItem.status,
    statusReason: bookNowItem.status_reason || undefined,
    rate: bookNowItem.rate,
    shipmentLink: bookNowItem.shipment_link || undefined,
  }
}

export function mapApiToBookNowStatus(bookNowStatus: RawBookNowStatus): BookNowStatus {
  const items: Array<BookNowItem> = (bookNowStatus.items ?? []).map(mapApiToBookNowItem)
  return {
    uuid: bookNowStatus.uuid,
    status: bookNowStatus.status,
    items: items,
  }
}

export function mapApiToCarrier(carrier: RawCarrier): Carrier {
  return {
    id: carrier.id,
    name: carrier.name || undefined,
    email: carrier.email || undefined,
    entityType: carrier.entity_type,
    dot: carrier.dot || undefined,
    mc: carrier.mc || undefined,
    scac: carrier.scac || undefined,
  }
}

export function mapApiToShipper(shipper: RawShipper): Shipper {
  return {
    id: shipper.id,
    email: shipper.email,
    logoUrl: shipper.logo_email_url || undefined,
    name: shipper.name || undefined,
    companyName: shipper.company_name,
    phoneNumber: shipper.phone_number || undefined,
  }
}

export function mapApiToStop(quoteStop: RawQuoteStop): Stop {
  return {
    address: quoteStop.address || undefined,
    city: quoteStop.city,
    country: quoteStop.country,
    date: quoteStop.date || undefined,
    state: quoteStop.state,
    zipCode: quoteStop.zipcode || undefined,
    fullStreetAddress: quoteStop.full_street_address || undefined,
    facilityName: quoteStop.facility?.company_name || undefined,
    appointment: mapApiToAppointment(quoteStop.appointment),
  }
}

export function mapApiToAccessorial(quoteAccessorial: RawQuoteAccessorial): QuoteAccessorial {
  return {
    name: quoteAccessorial.name,
    code: quoteAccessorial.code,
    stopType: quoteAccessorial.stop_type,
  }
}

export function mapEquipmentRequirements(
  equipmentRequirements: RawQuoteEquipmentRequirements
): QuoteEquipmentRequirements {
  const { with_tarp, tarp_type, tarp_size, dunnage, equipment_subtypes, temperature, unit } =
    equipmentRequirements

  return {
    withTarp: with_tarp,
    tarpType: tarp_type,
    tarpSize: tarp_size,
    dunnage: dunnage,
    equipmentSubtypes: equipment_subtypes?.[0],
    temperature: temperature,
    temperatureUnit: unit,
  }
}

export function mapApiToQuote(quote: RawQuote): Quote {
  const items: Array<ShippingItem> = mapApiToShippingItems(quote.items)
  const stops: Array<Stop> = (quote.stops ?? []).map(mapApiToStop)
  const accessorials: Array<QuoteAccessorial> = (quote.accessorials ?? []).map(mapApiToAccessorial)
  const requestedAccessorials: Array<PriceItemType> = (quote.requested_accessorials ?? []).map(
    mapApiToPriceItemType
  )
  const equipmentRequirements = quote?.equipment_requirements
    ? mapEquipmentRequirements(quote?.equipment_requirements)
    : undefined

  return {
    items,
    stops,
    bidsDeadline: createDate(quote.bids_deadline)!,
    canReceiveProposals: quote.can_receive_proposals,
    equipmentType: quote.equipment_type,
    equipmentRequirements,
    mileage: quote.mileage ?? undefined,
    measures: mapApiToShippingMeasures(quote.measures),
    mode: quote.mode,
    notes: quote.notes || undefined,
    shipperGuideId: quote.shipper_guide_id,
    shipperNotes: quote.shipper_notes || undefined,
    accessorials,
    requestedAccessorials,
    powerOnly: quote.power_only,
    targetRate: quote.target_rate,
    managedBy: mapApiToManagedBy(quote.managed_by),
    container: mapApiToContainer(quote.container),
  }
}

export function mapApiToRateLine(rawRateLine: RawRateLine): RateLine {
  return {
    quantity: Number(rawRateLine.quantity),
    subtype: rawRateLine.subtype,
    total: Number(rawRateLine.total),
    type: {
      value: rawRateLine.type.value,
      label: rawRateLine.type.label,
    },
    unitPrice: Number(rawRateLine.unit_price),
    notes: rawRateLine.notes,
    priceItemType: rawRateLine.price_item_type
      ? {
          uuid: rawRateLine.price_item_type.uuid,
          label: rawRateLine.price_item_type.label,
        }
      : undefined,
    stopType: rawRateLine.stop_type,
  }
}

export function mapApiToSpotQuote(rawSpotQuote: RawSpotQuote): SpotQuote {
  const carrier: Carrier = mapApiToCarrier(rawSpotQuote.carrier)
  const quote: Quote = mapApiToQuote(rawSpotQuote.quote)
  const shipper: Shipper = mapApiToShipper(rawSpotQuote.shipper)
  const ratelines: Array<RateLine> = (rawSpotQuote.ratelines ?? []).map(mapApiToRateLine)

  const spotQuote: SpotQuote = {
    carrier,
    quote,
    shipper,
    carrierNotes: rawSpotQuote.carrier_notes || undefined,
    flatRate: rawSpotQuote.flat_rate || undefined,
    notes: rawSpotQuote.notes || undefined,
    perMileRate: rawSpotQuote.per_mile_rate ?? undefined,
    status: rawSpotQuote.status,
    uuid: rawSpotQuote.uuid,
    negotiationNote: rawSpotQuote.negotiation_note || undefined,
    underNegotiation: rawSpotQuote.under_negotiation || false,
    bookNowEnabled: rawSpotQuote.book_now_enabled,
    cargoValue: rawSpotQuote.shipment_cargo_value ?? undefined,
    shipment: mapSpotQuoteShipmentFromApi(rawSpotQuote.shipment),
    ratelines,
    createdAt: new Date(rawSpotQuote.created_at),
    submittedAt: createDate(rawSpotQuote.submitted_at),
  }

  return spotQuote
}

export function mapSpotQuoteToApi(spotQuote: SubmitSpotQuoteParams): RawSubmitSpotQuoteParams {
  return {
    flat_rate: spotQuote.flatRate,
    notes: spotQuote.notes ?? '',
    accessorials: spotQuote.accessorials ?? [],
    requested_accessorials: (spotQuote.requestedAccessorials ?? []).map(
      requestedAccessorialCharge => ({
        price_item_type: requestedAccessorialCharge.priceItemType,
        stop_type: requestedAccessorialCharge.stopType,
        notes: requestedAccessorialCharge.notes,
        rate: requestedAccessorialCharge.rate,
        unit: requestedAccessorialCharge.unit,
      })
    ),
  }
}
